// Shared UI primitives for the messagerie, reproducing heric's design
// (Poppins, neutral palette, overlapping avatar stacks). Icons are heric's
// exact icons8 SVGs (copied into ./icons) rendered the same way as heric's
// Icon component.

import React from "react";

import { ReactComponent as IcPlus } from "./icons/ic-plus.svg";
import { ReactComponent as IcSearch } from "./icons/ic-search.svg";
import { ReactComponent as IcSend } from "./icons/ic-send.svg";
import { ReactComponent as IcClose } from "./icons/ic-close.svg";
import { ReactComponent as IcFile } from "./icons/ic-file.svg";
import { ReactComponent as IcJoin } from "./icons/ic-join.svg";
import { ReactComponent as IcDownload } from "./icons/ic-download.svg";
import { ReactComponent as IcResponse } from "./icons/ic-response.svg";
import { ReactComponent as IcModify } from "./icons/ic-modify.svg";
import { ReactComponent as IcDelete } from "./icons/ic-delete.svg";
import { ReactComponent as IcEye } from "./icons/ic-eye.svg";
import { ReactComponent as IcImage } from "./icons/ic-image.svg";

export const EMOJI_LIST = [
  "👍", "❤️", "😂", "🔥", "😍", "👏", "😮", "😢", "🙏", "💯", "✅", "🎉",
];

// App-wide default grey avatar (same as /organisation team, NavBar, HeaderBar…)
export const DEFAULT_AVATAR =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRR2dtSs0-ybB7OnCnfsC1UWgViGQLxAtVx83IPHmxIjh1istqxGhm2sjWOSF1TDN_J-r0&usqp=CAU";

// Map a hexagone user doc to the shape the UI expects.
export function mapMember(u) {
  if (!u) return null;
  return {
    id: u.id,
    name: u.display_name || u.full_name || u.name || u.email || "Inconnu",
    role: u.role || u.poste || "",
    online: false,
    photo_url: u.photo || u.photo_url || u.avatar_url || undefined,
  };
}

export function getMember(members, id) {
  const u = (members || []).find((m) => m.id === id);
  return u ? mapMember(u) : null;
}

export function MemberAvatar({ member, size = 24 }) {
  return (
    <img
      src={member?.photo_url || DEFAULT_AVATAR}
      alt={member?.name || "membre"}
      className="rounded-full flex-shrink-0 object-cover bg-white"
      style={{ width: size, height: size }}
      onError={(e) => {
        if (e.currentTarget.src !== DEFAULT_AVATAR) e.currentTarget.src = DEFAULT_AVATAR;
      }}
    />
  );
}

export function AvatarStack({ members, size = 24, maxVisible = 3, fixedWidth }) {
  const visible = (members || []).slice(0, maxVisible);
  const hiddenCount = (members || []).length - maxVisible;
  const overlap = size * 0.58;
  return (
    <div
      className="flex flex-row items-center flex-shrink-0"
      style={fixedWidth ? { width: fixedWidth } : undefined}
    >
      {visible.map((m, i) => (
        <div
          key={m.id}
          className="flex-shrink-0"
          style={{ marginLeft: i === 0 ? 0 : -overlap, zIndex: maxVisible - i + 1 }}
        >
          <MemberAvatar member={m} size={size} />
        </div>
      ))}
      {hiddenCount > 0 && (
        <span style={{ marginLeft: 2, fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#A1A1A1" }}>
          +{hiddenCount}
        </span>
      )}
    </div>
  );
}

// --- Time formatting ---

export function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function formatListTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
}

// --- Icon: heric's exact icons8 SVGs, rendered like heric's Icon1 ---

const SVGS = {
  plus: IcPlus,
  search: IcSearch,
  send: IcSend,
  close: IcClose,
  file: IcFile,
  join: IcJoin,
  download: IcDownload,
  response: IcResponse,
  modify: IcModify,
  delete: IcDelete,
  eye: IcEye,
  image: IcImage,
};

export function Icon({ name, size = 16, color = "currentColor" }) {
  // Small inline glyphs not part of heric's icons8 set.
  if (name === "chevron") {
    return (
      <svg width={size} height={size * 0.6} viewBox="0 0 10 6" fill="none" style={{ color, flexShrink: 0 }}>
        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "check") {
    return (
      <svg width={size} height={size} viewBox="0 0 12 12" fill="none" style={{ color, flexShrink: 0 }}>
        <path d="M2 6.5L4.8 9L10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "dots") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ color, flexShrink: 0 }}>
        <circle cx="3" cy="8" r="1.5" fill="currentColor" />
        <circle cx="8" cy="8" r="1.5" fill="currentColor" />
        <circle cx="13" cy="8" r="1.5" fill="currentColor" />
      </svg>
    );
  }
  if (name === "pin") {
    // Thumbtack / pin glyph (rotated like WhatsApp's pinned indicator).
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ color, flexShrink: 0 }}>
        <path d="M9.5 1.8l4.7 4.7-1.3.4a2 2 0 0 0-1 .55L8.3 11.8l-4.1-4.1 3.6-3.6a2 2 0 0 0 .55-1l.4-1.3z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M6.2 9.8L2.5 13.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "bell" || name === "bell-off") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ color, flexShrink: 0 }}>
        <path d="M8 2c-2.2 0-3.8 1.8-3.8 4 0 3.2-1 4-1 4h9.6s-1-.8-1-4c0-2.2-1.6-4-3.8-4z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M6.7 12.3a1.4 1.4 0 0 0 2.6 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        {name === "bell-off" && (
          <path d="M2.5 2.5l11 11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        )}
      </svg>
    );
  }
  const Cmp = SVGS[name];
  if (!Cmp) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>
      <Cmp style={{ width: "100%", height: "100%", color, fill: color, stroke: color }} />
    </span>
  );
}
